import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, HelpCircle, ChevronRight } from 'lucide-react';
import { motion } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { FAQ_DATA } from '@/pages/faq';


export const HomeFAQSection: React.FC = () => {
  const HOME_FAQS = useMemo(() =>
    FAQ_DATA.flatMap(category => category.questions).slice(0, 6),
  []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": HOME_FAQS.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section className="py-32 bg-white overflow-hidden border-t border-black/5" id="home-faq">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
          <div className="max-w-2xl">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-cyan-600 mb-4 block">Help Center</span>
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6 tracking-tight uppercase font-display">
              <span className="text-zinc-400">Common</span> <br />
              Questions
            </h2>
            <p className="text-zinc-500 text-lg leading-relaxed font-sans">
              Find answers to the most frequent questions about our platform, payments, and creative tools.
            </p>
          </div>
          
          <Link
            to="/faq"
            className="inline-flex items-center gap-2 text-black font-extrabold text-xs uppercase tracking-widest hover:gap-4 transition-all group font-sans"
          >
            Visit FAQ Page
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {HOME_FAQS.map((faq, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="group p-8 rounded-[2rem] bg-zinc-50 border border-zinc-100 hover:border-cyan-500 hover:bg-white hover:shadow-2xl hover:shadow-cyan-500/5 transition-all duration-500"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-2xl bg-white border border-zinc-100 flex items-center justify-center shrink-0 group-hover:bg-cyan-600 group-hover:text-white transition-colors">
                  <HelpCircle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-black mb-3 leading-tight">{faq.question}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed font-sans font-medium">{faq.answer}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="mt-12 text-center md:hidden">
            <Link
                to="/faq"
                className="inline-flex items-center gap-2 text-cyan-600 font-extrabold text-xs uppercase tracking-widest hover:gap-4 transition-all group font-sans border-b-2 border-cyan-100 pb-1"
            >
                View all questions
                <ChevronRight className="w-4 h-4" />
            </Link>
        </div>
      </div>
    </section>
  );
};
